import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';

export type MapCursorType =
    | 'default' // Default browser cursor
    | 'pointer' // Clickable elements (markers)
    | 'crosshair' // Adding custom pins
    | 'grab' // Map panning (default)
    | 'grabbing' // Map panning (active)
    | 'not-allowed'; // Disabled state

// Map cursor types to CSS cursor values
const CURSOR_MAP: Record<MapCursorType, string> = {
    default: '',
    pointer: 'pointer',
    crosshair: 'crosshair',
    grab: 'grab',
    grabbing: 'grabbing',
    'not-allowed': 'not-allowed',
} as const;

export function useMapCursor() {
    const { map } = useMapContext();
    const currentCursorRef = useRef<MapCursorType>('grab');

    const mapRef = useRef<maplibregl.Map | null>(null);

    useEffect(() => {
        mapRef.current = map;
    }, [map]);

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.getCanvas().style.cursor = CURSOR_MAP.grab;
            currentCursorRef.current = 'grab';
        }
    }, []);

    const setCursor = useCallback((type: MapCursorType) => {
        if (!mapRef.current) {
            return;
        }

        const cssCursor = CURSOR_MAP[type];
        mapRef.current.getCanvas().style.cursor = cssCursor;
        currentCursorRef.current = type;
    }, []);

    const getCurrentCursor = useCallback(() => currentCursorRef.current, []);

    return {
        setCursor,
        getCurrentCursor,
    } as const;
}
